import { Box, Stack } from '@mui/material'
import { ColumnDef, Row } from '@tanstack/react-table'
import type { MonetaryValueDataObjectType } from 'ComponentLibrary/src/common/money'
import { decimalToLocalizedString, tokens } from 'ComponentLibrary'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { SimpleTable } from 'ComponentLibrary/src/piazza/tables/SimpleTable/SimpleTable'
import { SimpleTableCell } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableCell'
import {
    SimpleTableMonetaryCell,
    SimpleTablePercentageCell,
    SimpleTableTextCell,
} from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTextCells'
import invariant from 'tiny-invariant'
import { SimpleTableHeadline } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableHeadline'

type MatrixRow = {
    yieldLabelValue: string
    yieldValue?: number
    values: Array<MonetaryValueDataObjectType | null>
}

export type ValueCheckSensitivityMatrix = {
    market_values?: MonetaryValueDataObjectType[]
    net_yields?: number[]
    values?: Array<Array<MonetaryValueDataObjectType | null>>
}

type ValueCheckSensitivityMatrixWidgetProps = {
    sensitivityMatrix: ValueCheckSensitivityMatrix
    copy?: ValueCheckSensitivityMatrixWidgetCopy
    locale?: string
}

const yieldHeaderDeltaValues = [20, 10, 0, -10, -20]

export type ValueCheckSensitivityMatrixWidgetCopy = {
    title: string
    netYieldLabel: string
    marketRentLabel: string
    percentagePointUnit?: string
}

const DEFAULT_VALUE_CHECK_SENSITIVITY_MATRIX_WIDGET_COPY: ValueCheckSensitivityMatrixWidgetCopy = {
    title: 'Sensitivitätsmatrix',
    netYieldLabel: 'Nettorendite',
    marketRentLabel: 'Marktmiete',
    percentagePointUnit: 'BP',
}

const formatYieldHeaderValue = (deltaValue: number, locale: string) => {
    const sign = deltaValue < 0 ? '-' : '+'
    const absoluteDeltaValue = Math.abs(deltaValue)

    return `${sign}${decimalToLocalizedString(absoluteDeltaValue, false, 0, 0, locale)}`
}

const getGridCellBackground = (rowIndex: number, columnIndex: number, middleRowIndex: number, middleColumnIndex: number) => {
    if (rowIndex === columnIndex) {
        if (rowIndex === middleRowIndex) {
            return '#F6F6F6'
        }
        return undefined
    }
    const isAboveDiagonal = rowIndex < columnIndex
    const isMiddleAxis = rowIndex === middleRowIndex || columnIndex === middleColumnIndex

    if (isAboveDiagonal) {
        return isMiddleAxis ? '#F6E8E8' : '#FFF1F0'
    }

    return isMiddleAxis ? '#E8F1EE' : '#F0FAF7'
}

export default function ValueCheckSensitivityMatrixWidget({
    sensitivityMatrix,
    copy = DEFAULT_VALUE_CHECK_SENSITIVITY_MATRIX_WIDGET_COPY,
    locale = 'de-DE',
}: ValueCheckSensitivityMatrixWidgetProps) {
    const marketValues = sensitivityMatrix.market_values ?? []
    const netYields = sensitivityMatrix.net_yields ?? []
    const matrixValues = sensitivityMatrix.values ?? []
    const middleRowIndex = Math.floor(netYields.length / 2)
    const middleColumnIndex = Math.floor(marketValues.length / 2)
    const percentagePointUnit = copy.percentagePointUnit ?? 'BP'
    const marketHeaderLabels = ['+10%', '+5%', '+0%', '-5%', '-10%'].map((delta) => `${copy.marketRentLabel} ${delta}`)

    invariant(
        marketValues.length === 5 && netYields.length === 5 && matrixValues.length === 5 && matrixValues.every((row) => row.length === 5),
        'Sensitivity matrix must be 5x5.'
    )

    const rows: MatrixRow[] = netYields.map((yieldValue, index) => ({
        yieldLabelValue: formatYieldHeaderValue(
            yieldHeaderDeltaValues[index] ?? yieldHeaderDeltaValues[yieldHeaderDeltaValues.length - 1],
            locale
        ),
        yieldValue,
        values: matrixValues[index] ?? [],
    }))

    const columns: ColumnDef<MatrixRow>[] = [
        {
            id: 'yield-label-group',
            header: () => <SimpleTableCell />,
            columns: [
                {
                    id: 'yield-label',
                    header: () => <SimpleTableHeadline text={copy.netYieldLabel} variant="secondary" />,
                    cell: ({ row }) => (
                        <Box
                            sx={{
                                width: '100%',
                                bgcolor: row.index === middleRowIndex ? '#F6F6F6' : undefined,
                            }}
                        >
                            <SimpleTableTextCell
                                text={row.original.yieldLabelValue}
                                unit={percentagePointUnit}
                                emphasis={row.index === middleRowIndex}
                                alignment="right"
                            />
                        </Box>
                    ),
                    size: 130,
                },
            ],
        },
        {
            id: 'yield-value-group',
            header: () => <SimpleTableCell />,
            columns: [
                {
                    id: 'yield-value',
                    header: () => <SimpleTableCell />,
                    cell: ({ row }) => (
                        <Box
                            sx={{
                                width: '100%',
                                bgcolor: row.index === middleRowIndex ? '#F6F6F6' : undefined,
                            }}
                        >
                            <SimpleTablePercentageCell
                                percentage={row.original.yieldValue}
                                emphasis={row.index === middleRowIndex}
                                alignment="right"
                                locale={locale}
                            />
                        </Box>
                    ),
                    size: 90,
                },
            ],
        },
        ...marketValues.map((marketValue, index) => ({
            id: `market-group-${index}`,
            header: () => (
                <Box
                    sx={{
                        width: '100%',
                        bgcolor: index === middleColumnIndex ? '#F6F6F6' : undefined,
                    }}
                >
                    <SimpleTableHeadline
                        text={marketHeaderLabels[index] ?? marketHeaderLabels[marketHeaderLabels.length - 1]}
                        variant="secondary"
                    />
                </Box>
            ),
            columns: [
                {
                    id: `market-value-${index}`,
                    header: () => (
                        <Box
                            sx={{
                                width: '100%',
                                bgcolor: index === middleColumnIndex ? '#F6F6F6' : undefined,
                            }}
                        >
                            <SimpleTableMonetaryCell
                                monetaryValue={marketValue}
                                emphasis={index === middleColumnIndex}
                                alignment="right"
                                rounded
                                locale={locale}
                            />
                        </Box>
                    ),
                    cell: ({ row }: { row: Row<MatrixRow> }) => {
                        const value = row.original.values[index]
                        const backgroundColor = getGridCellBackground(row.index, index, middleRowIndex, middleColumnIndex)
                        return (
                            <Box sx={{ width: '100%', bgcolor: backgroundColor }}>
                                {value ? (
                                    <SimpleTableMonetaryCell
                                        monetaryValue={value}
                                        emphasis={row.index === index && index === middleColumnIndex}
                                        rounded
                                        alignment="right"
                                        locale={locale}
                                    />
                                ) : (
                                    <SimpleTableMonetaryCell alignment="right" locale={locale} />
                                )}
                            </Box>
                        )
                    },
                },
            ],
        })),
    ]

    return (
        <Stack direction="column" gap={tokens.SizingSpacing200}>
            <Headline title={copy.title} variant="headline3" />
            <SimpleTable data={rows} columns={columns} width="100%" backgroundColor={tokens.ColorSurfaceDefault} disableVirtualization />
        </Stack>
    )
}
